<template>
  <div class="page-wrapper">
    <Header />
    
    <main class="main-content">
      <div class="container form-container">
        
        <!-- Header -->
        <header class="form-page-header">
          <router-link to="/old-books" class="back-link">
            <icon icon="fa-solid fa-arrow-left" /> Chợ sách cũ
          </router-link>
          <h1 class="form-page-title font-serif">{{ isEditMode ? 'Chỉnh sửa gian hàng sách cũ' : 'Đăng bán gian hàng sách cũ' }}</h1>
          <p class="form-page-subtitle">Đăng bán 1 cuốn hoặc tạo combo lên đến 8 cuốn sách dễ dàng và trực quan.</p>
        </header>

        <!-- Progress Stepper -->
        <div class="stepper">
          <div class="step" :class="{ active: activeStep === 1, done: activeStep > 1 }" @click="activeStep > 1 && !isSubmitting ? activeStep = 1 : null">
            <span class="step-num">
              <icon v-if="activeStep > 1" icon="fa-solid fa-check" />
              <span v-else>1</span>
            </span>
            <span class="step-label">Thông tin gian hàng & Sách</span>
          </div>
          <div class="step-line" :class="{ filled: activeStep >= 2 }"></div>
          <div class="step" :class="{ active: activeStep === 2, done: activeStep > 2 }">
            <span class="step-num">
              <icon v-if="activeStep > 2" icon="fa-solid fa-check" />
              <span v-else>2</span>
            </span>
            <span class="step-label">Xem trước bài đăng</span>
          </div>
          <div class="step-line" :class="{ filled: activeStep >= 3 }"></div>
          <div class="step" :class="{ active: activeStep === 3, done: activeStep === 3 }">
            <span class="step-num">
              <icon v-if="activeStep === 3" icon="fa-solid fa-check" />
              <span v-else>3</span>
            </span>
            <span class="step-label">Hoàn tất</span>
          </div>
        </div>

        <!-- STEP 1: FORM INPUTS -->
        <div v-if="activeStep === 1" class="form-layout-step1">
          
          <!-- Section 1: Cấu hình gian hàng & Hình thức bán -->
          <section class="form-card booth-config-card">
            <div class="form-card-header">
              <div class="d-flex align-items-center gap-2">
                <span class="card-step-badge">1</span>
                <h2 class="form-card-title">Cấu hình gian hàng & Hình thức bán</h2>
              </div>
            </div>

            <div class="row g-4">
              <!-- Sale Type Selection -->
              <div class="col-md-6">
                <label class="field-label">Hình thức bán <span class="req">*</span></label>
                <div class="sale-type-options">
                  <div 
                    class="sale-type-card" 
                    :class="{ active: boothForm.saleType === 0 }"
                    @click="selectSaleType(0)"
                  >
                    <div class="type-card-radio">
                      <span class="radio-dot" v-if="boothForm.saleType === 0"></span>
                    </div>
                    <div class="type-card-info">
                      <strong class="type-card-title">Cho phép mua lẻ từng cuốn</strong>
                      <span class="type-card-desc">Người mua có thể chọn mua 1 cuốn hoặc mua cả bộ. Bạn sẽ nhập giá bán cho từng cuốn.</span>
                    </div>
                  </div>

                  <div 
                    class="sale-type-card" 
                    :class="{ active: boothForm.saleType === 1 }"
                    @click="selectSaleType(1)"
                  >
                    <div class="type-card-radio">
                      <span class="radio-dot" v-if="boothForm.saleType === 1"></span>
                    </div>
                    <div class="type-card-info">
                      <strong class="type-card-title">Bắt buộc mua cả combo (từ 2 cuốn)</strong>
                      <span class="type-card-desc">Người mua phải mua toàn bộ số sách trong bài đăng (từ 2 đến 8 cuốn). Bạn chỉ cần nhập 1 giá trọn gói combo.</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Book Count Selector & Weight -->
              <div class="col-md-6">
                <!-- Book Count Selector -->
                <div class="field-group">
                  <label class="field-label">
                    Số lượng sách trong gian hàng <span class="req">*</span>
                    <span class="field-hint">(Tối đa 8 cuốn{{ boothForm.saleType === 1 ? ' • Combo tối thiểu 2 cuốn' : '' }})</span>
                  </label>
                  <div class="book-count-selector">
                    <button 
                      type="button" 
                      v-for="n in 8" 
                      :key="n"
                      class="count-pill-btn"
                      :class="{ active: boothForm.totalBooks === n, disabled: boothForm.saleType === 1 && n === 1 }"
                      :disabled="boothForm.saleType === 1 && n === 1"
                      :title="boothForm.saleType === 1 && n === 1 ? 'Hình thức combo yêu cầu tối thiểu 2 cuốn' : ''"
                      @click="setBookCount(n)"
                    >
                      {{ n }} {{ n === 1 ? 'cuốn' : '' }}
                    </button>
                  </div>
                </div>

                <!-- Estimated Weight -->
                <div class="field-group mt-3">
                  <label class="field-label">Khối lượng ước tính (gram) <span class="field-hint">(để tính phí ship)</span></label>
                  <div class="input-with-icon">
                    <icon icon="fa-solid fa-weight-hanging" class="field-icon" />
                    <input 
                      type="number" 
                      v-model.number="boothForm.weight" 
                      min="0"
                      class="field-input" 
                      placeholder="Ví dụ: 450 (gram)" 
                    />
                    <span class="input-suffix">gram</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Title & Description & Stack Image Upload -->
            <div class="row g-4 mt-1">
              <div class="col-md-6">
                <!-- Booth Title -->
                <div class="field-group">
                  <label class="field-label">Tiêu đề gian hàng <span class="field-hint">(tùy chọn)</span></label>
                  <input 
                    type="text" 
                    v-model="boothForm.name" 
                    class="field-input font-serif" 
                    placeholder="VD: Combo 3 cuốn Nguyễn Nhật Ánh, Bộ sách Harry Potter..." 
                  />
                </div>

                <!-- Combo Price Input if COMBO_ONLY -->
                <div class="field-group mt-3" v-if="boothForm.saleType === 1">
                  <label class="field-label">Giá trọn bộ Combo (VNĐ) <span class="req">*</span></label>
                  <div class="input-with-prefix">
                    <span class="field-prefix">₫</span>
                    <input 
                      type="text" 
                      v-model="formattedComboPrice" 
                      class="field-input price-input-highlight" 
                      placeholder="VD: 150.000" 
                    />
                  </div>
                </div>

                <!-- Calculated Total Price if ALLOW_SINGLE -->
                <div class="field-group mt-3" v-else>
                  <div class="calculated-price-box">
                    <span class="calc-label">Tổng giá trị gian hàng (tự động tính từ các cuốn sách):</span>
                    <strong class="calc-value">{{ formatPrice(calculatedTotalPrice) }}</strong>
                  </div>
                </div>

                <!-- Sale Off Auto Indicator -->
                <div class="sale-off-alert-box mt-3" v-if="calculatedDiscountRate > 0">
                  <icon icon="fa-solid fa-tags" class="sale-off-alert-icon" />
                  <div>
                    <strong>Tự động áp dụng Sale Off -{{ calculatedDiscountRate }}%!</strong>
                    <p class="m-0 fs-8 text-muted">Vì tất cả {{ boothForm.totalBooks }} cuốn sách đều có giá bìa gốc (Tổng giá bìa: {{ formatPrice(calculatedTotalOriginalPrice) }} $\rightarrow$ Giá bán: {{ formatPrice(effectivePrice) }}).</p>
                  </div>
                </div>
              </div>

              <!-- Stack Image Upload (Optional for >= 2 books) -->
              <div class="col-md-6">
                <div class="field-group" v-if="boothForm.totalBooks >= 2">
                  <label class="field-label">
                    Ảnh chụp chồng sách <span class="field-hint">(không bắt buộc)</span>
                  </label>
                  <div 
                    class="stack-dropzone"
                    :class="{ filled: stackPreview }"
                    @click="triggerStackUpload"
                    @dragover.prevent
                    @drop.prevent="handleStackDrop"
                  >
                    <input 
                      type="file" 
                      ref="stackInput" 
                      accept="image/*" 
                      class="d-none" 
                      @change="handleStackFileChange" 
                    />
                    <template v-if="stackPreview">
                      <img :src="stackPreview" alt="Ảnh chồng sách" class="stack-preview-img" @error="handleImageError" />
                      <button type="button" class="slot-remove" @click.stop="removeStackImage" title="Xóa ảnh chồng sách">
                        <icon icon="fa-solid fa-xmark" />
                      </button>
                    </template>
                    <template v-else>
                      <icon icon="fa-solid fa-camera" class="stack-drop-icon" />
                      <strong>Chụp hoặc kéo thả ảnh chồng sách vào đây</strong>
                      <span>Nếu có ảnh này, gian hàng sẽ ưu tiên hiển thị ảnh chồng sách ở ngoài trang chủ</span>
                    </template>
                  </div>
                </div>

                <!-- Booth Description -->
                <div class="field-group" :class="{ 'mt-3': boothForm.totalBooks >= 2 }">
                  <label class="field-label">Mô tả chung gian hàng <span class="field-hint">(tùy chọn)</span></label>
                  <textarea 
                    v-model="boothForm.description" 
                    rows="3" 
                    class="field-textarea"
                    placeholder="Ghi chú về combo, đóng gói, quà tặng kèm nếu có..."
                  ></textarea>
                </div>
              </div>
            </div>
          </section>

          <!-- Section 2: Danh sách từng cuốn sách trong gian hàng -->
          <section class="form-card booth-books-card">
            <div class="form-card-header">
              <div class="d-flex align-items-center gap-2">
                <span class="card-step-badge">2</span>
                <h2 class="form-card-title">Thông tin & Tình trạng từng cuốn sách</h2>
              </div>
              <span class="upload-counter">
                {{ boothForm.totalBooks }} cuốn sách
              </span>
            </div>

            <!-- Book Navigation Tabs -->
            <div class="book-tabs-nav" v-if="boothForm.totalBooks > 1">
              <button 
                type="button" 
                v-for="(book, idx) in books" 
                :key="'tab-' + idx"
                class="book-tab-btn"
                :class="{ active: currentBookIndex === idx, complete: isBookComplete(idx) }"
                @click="currentBookIndex = idx"
              >
                <span class="tab-num">{{ idx + 1 }}</span>
                <span class="tab-title">{{ book.name ? book.name : ('Cuốn ' + (idx + 1)) }}</span>
                <icon v-if="isBookComplete(idx)" icon="fa-solid fa-check" class="tab-check-icon" />
              </button>
            </div>

            <!-- Active Book Form Block -->
            <div class="active-book-form-wrap" v-if="currentBook">
              <div class="row g-4">
                <!-- Left: Catalog lookup, Name, Author/Page, Category, Price, Description -->
                <div class="col-lg-6">
                  
                  <!-- Catalog Search Lookup -->
                  <div class="field-group">
                    <label class="field-label">
                      Tìm sách trên hệ thống <span class="field-hint">(tùy chọn - tự động điền)</span>
                    </label>
                    <div class="input-with-icon custom-catalog-lookup-wrapper">
                      <icon icon="fa-solid fa-magnifying-glass" class="field-icon" />
                      <input 
                        type="text" 
                        v-model="catalogSearchKey" 
                        @input="onCatalogInput"
                        @focus="onCatalogInput"
                        class="field-input" 
                        placeholder="Gõ tên cuốn sách để tìm kiếm..." 
                      />
                      <button v-if="catalogSearchKey" type="button" class="field-clear" @click="clearCatalogSelection">
                        <icon icon="fa-solid fa-xmark" />
                      </button>

                      <!-- Dropdown suggestions -->
                      <div v-if="showCatalogDropdown" class="suggest-dropdown">
                        <div v-if="isSearchingCatalog" class="suggest-loading">
                          <span class="spinner-border spinner-border-sm text-emerald me-2"></span>
                          <span>Đang tra cứu hệ thống...</span>
                        </div>
                        <div v-else-if="catalogSuggestions.length > 0" class="suggest-list">
                          <div 
                            v-for="b in catalogSuggestions" 
                            :key="'suggest-' + b.id" 
                            class="suggest-item"
                            @click="selectCatalogBook(b)"
                          >
                            <img v-if="b.cover" :src="b.cover" class="suggest-cover" alt="" @error="b.cover = null" />
                            <div class="suggest-info">
                              <h4 class="suggest-title">{{ b.name }}</h4>
                              <span class="suggest-author">{{ getAuthorsString(b) }}</span>
                            </div>
                            <span class="btn-select-chip">Chọn</span>
                          </div>
                        </div>
                        <div v-else class="suggest-empty">
                          <span>Không tìm thấy sách trong catalog. Bạn có thể tự nhập bên dưới.</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Book Name -->
                  <div class="field-group">
                    <label class="field-label">Tên cuốn sách <span class="req">*</span></label>
                    <input 
                      type="text" 
                      v-model="currentBook.name" 
                      class="field-input font-serif" 
                      required 
                      placeholder="Nhập tên cuốn sách..." 
                    />
                  </div>

                  <!-- Author & Pages Row -->
                  <div class="row g-3">
                    <div class="col-sm-7">
                      <div class="field-group">
                        <label class="field-label">Tác giả <span class="field-hint">(tùy chọn)</span></label>
                        <div class="input-with-icon">
                          <icon icon="fa-solid fa-feather-pointed" class="field-icon" />
                          <input 
                            type="text" 
                            v-model="currentBook.author" 
                            class="field-input" 
                            placeholder="Tác giả (VD: Nguyễn Nhật Ánh)..." 
                          />
                        </div>
                      </div>
                    </div>
                    <div class="col-sm-5">
                      <div class="field-group">
                        <label class="field-label">Số trang <span class="field-hint">(tùy chọn)</span></label>
                        <div class="input-with-icon">
                          <icon icon="fa-solid fa-book-open" class="field-icon" />
                          <input 
                            type="number" 
                            v-model.number="currentBook.totalPage" 
                            min="0"
                            class="field-input" 
                            placeholder="VD: 320" 
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Category Multi-Select -->
                  <div class="field-group">
                    <div class="d-flex align-items-center justify-content-between mb-1">
                      <label class="field-label m-0">Thể loại sách <span class="field-hint">(tùy chọn)</span></label>
                      <span v-if="selectedCategoriesList.length > 0" class="category-selected-badge">
                        Đã chọn {{ selectedCategoriesList.length }} thể loại
                      </span>
                    </div>

                    <div class="category-picker-box custom-category-select-wrapper" :class="{ 'is-open': showCategoryDropdown }">
                      <!-- Trigger area -->
                      <div 
                        class="category-picker-trigger"
                        @click="toggleCategoryDropdown"
                      >
                        <div class="picker-icon-wrap">
                          <icon icon="fa-solid fa-tags" />
                        </div>

                        <div class="picker-content">
                          <div v-if="selectedCategoriesList.length > 0" class="category-chips-list">
                            <span 
                              v-for="cat in selectedCategoriesList" 
                              :key="'cat-' + cat.value" 
                              class="category-chip"
                              @click.stop
                            >
                              <span class="chip-text">{{ cat.label }}</span>
                              <button 
                                type="button" 
                                class="chip-close-btn" 
                                @click.stop="removeCategoryTag(cat.value)"
                                title="Bỏ chọn"
                              >
                                <icon icon="fa-solid fa-xmark" />
                              </button>
                            </span>
                          </div>
                          <span v-else class="picker-placeholder">
                            {{ isCategoriesLoading ? 'Đang tải danh sách thể loại...' : 'Chọn một hoặc nhiều thể loại sách...' }}
                          </span>
                        </div>

                        <div class="picker-arrow-wrap">
                          <icon icon="fa-solid fa-chevron-down" class="picker-chevron" />
                        </div>
                      </div>

                      <!-- Dropdown Menu -->
                      <div v-if="showCategoryDropdown" class="category-dropdown-panel" @click.stop>
                        <!-- Header Search inside Dropdown -->
                        <div class="dropdown-search-bar">
                          <icon icon="fa-solid fa-magnifying-glass" class="search-bar-icon" />
                          <input 
                            type="text" 
                            v-model="categorySearchQuery" 
                            class="dropdown-search-input" 
                            placeholder="Gõ để lọc nhanh thể loại..." 
                            ref="categorySearchInput"
                          />
                          <button 
                            v-if="categorySearchQuery" 
                            type="button" 
                            class="btn-clear-search" 
                            @click="categorySearchQuery = ''"
                          >
                            <icon icon="fa-solid fa-xmark" />
                          </button>
                        </div>

                        <!-- Options List (Grid Layout) -->
                        <div class="dropdown-options-grid">
                          <div 
                            v-for="c in filteredCategoryOptions" 
                            :key="c.value" 
                            class="category-option-card"
                            :class="{ selected: isCategorySelected(c.value) }"
                            @click.stop="toggleCategory(c.value)"
                          >
                            <div class="option-check-circle" :class="{ checked: isCategorySelected(c.value) }">
                              <icon v-if="isCategorySelected(c.value)" icon="fa-solid fa-check" />
                            </div>
                            <span class="option-label-text">{{ c.label }}</span>
                          </div>

                          <div v-if="filteredCategoryOptions.length === 0" class="dropdown-empty-state">
                            <icon icon="fa-solid fa-magnifying-glass" class="mb-1 opacity-50" />
                            <span>Không tìm thấy thể loại "{{ categorySearchQuery }}"</span>
                          </div>
                        </div>

                        <!-- Dropdown Footer -->
                        <div class="dropdown-footer-bar">
                          <span class="footer-selected-count">
                            Đã chọn: <strong>{{ selectedCategoriesList.length }}</strong> thể loại
                          </span>
                          <div class="d-flex gap-2">
                            <button 
                              v-if="selectedCategoriesList.length > 0"
                              type="button" 
                              class="btn-dropdown-clear-all"
                              @click.stop="clearAllCategories"
                            >
                              Bỏ chọn hết
                            </button>
                            <button 
                              type="button" 
                              class="btn-dropdown-done"
                              @click.stop="showCategoryDropdown = false"
                            >
                              Xong
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Prices Row (Only when Cho phép mua lẻ) -->
                  <div class="row g-3" v-if="boothForm.saleType === 0">
                    <div class="col-6">
                      <div class="field-group">
                        <label class="field-label">Giá bìa gốc <span class="field-hint">(tùy chọn)</span></label>
                        <div class="input-with-prefix">
                          <span class="field-prefix">₫</span>
                          <input 
                            type="text" 
                            :value="formatPriceInput(currentBook.originalPrice)" 
                            @input="onOriginalPriceInput"
                            class="field-input" 
                            placeholder="VD: 100.000" 
                          />
                        </div>
                      </div>
                    </div>

                    <div class="col-6">
                      <div class="field-group">
                        <label class="field-label">
                          Giá bán lẻ cuốn này <span class="req">*</span>
                        </label>
                        <div class="input-with-prefix">
                          <span class="field-prefix">₫</span>
                          <input 
                            type="text" 
                            :value="formatPriceInput(currentBook.price)" 
                            @input="onBookPriceInput"
                            class="field-input border-emerald" 
                            placeholder="VD: 50.000" 
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Description for this book -->
                  <div class="field-group mt-2">
                    <div class="field-label-row">
                      <label class="field-label m-0">Mô tả tình trạng cuốn này <span class="req">*</span></label>
                      <button type="button" class="btn-template-sm" @click="insertTemplateDescription">
                        <icon icon="fa-solid fa-wand-magic-sparkles" /> Dùng mẫu
                      </button>
                    </div>
                    <textarea 
                      v-model="currentBook.description" 
                      rows="3" 
                      class="field-textarea" 
                      required 
                      placeholder="Mô tả kỹ tình trạng: gáy sách, vết gấp, ố vàng, trang có bị gạch chân không..."
                    ></textarea>
                  </div>

                </div>

                <!-- Right: Condition Rating & 2 Photos (Front + Back) -->
                <div class="col-lg-6">
                  
                  <!-- Condition Pills -->
                  <div class="field-group">
                    <label class="field-label">Tình trạng sách <span class="req">*</span></label>
                    <div class="condition-pills-grid">
                      <button 
                        type="button"
                        v-for="opt in conditionOptions" 
                        :key="opt.value" 
                        class="cond-pill-sm"
                        :class="{ active: currentBook.verStatus === opt.value }"
                        @click="selectCondition(opt.value)"
                      >
                        <strong>{{ opt.label }}</strong>
                        <small>{{ opt.range }}</small>
                      </button>
                    </div>
                  </div>

                  <!-- Percentage Slider -->
                  <div class="field-group mt-2">
                    <div class="field-label-row">
                      <label class="field-label m-0">Ước tính độ mới</label>
                      <span class="field-value-badge">{{ currentBook.perStatus }}%</span>
                    </div>
                    <input type="range" v-model.number="currentBook.perStatus" min="30" max="100" class="range-slider" />
                  </div>

                  <!-- 2 Photos Slots: Cover Front + Cover Back -->
                  <div class="field-group mt-3">
                    <label class="field-label">
                      Hình ảnh thực tế <span class="req">*</span>
                      <span class="field-hint">(Bìa trước & Bìa sau)</span>
                    </label>

                    <div class="two-photos-grid">
                      <!-- Front Cover -->
                      <div class="book-photo-slot">
                        <span class="slot-title">Bìa trước <span class="req">*</span></span>
                        <div 
                          class="slot-area-box"
                          :class="{ filled: currentBookPreview.cover }"
                          @click="triggerBookUpload('cover')"
                          @dragover.prevent
                          @drop.prevent="e => handleBookDrop(e, 'cover')"
                        >
                          <input type="file" ref="bookCoverInput" class="d-none" accept="image/*" @change="e => handleBookFileChange(e, 'cover')" />
                          <template v-if="currentBookPreview.cover">
                            <img :src="currentBookPreview.cover" alt="Bìa trước" @error="handleImageError" />
                            <button type="button" class="slot-remove" @click.stop="removeBookImage('cover')"><icon icon="fa-solid fa-xmark" /></button>
                          </template>
                          <template v-else>
                            <icon icon="fa-solid fa-plus" class="slot-add-icon" />
                            <span>Tải ảnh bìa trước</span>
                          </template>
                        </div>
                      </div>

                      <!-- Back Cover -->
                      <div class="book-photo-slot">
                        <span class="slot-title">Bìa sau <span class="req">*</span></span>
                        <div 
                          class="slot-area-box"
                          :class="{ filled: currentBookPreview.coverBack }"
                          @click="triggerBookUpload('coverBack')"
                          @dragover.prevent
                          @drop.prevent="e => handleBookDrop(e, 'coverBack')"
                        >
                          <input type="file" ref="bookCoverBackInput" class="d-none" accept="image/*" @change="e => handleBookFileChange(e, 'coverBack')" />
                          <template v-if="currentBookPreview.coverBack">
                            <img :src="currentBookPreview.coverBack" alt="Bìa sau" @error="handleImageError" />
                            <button type="button" class="slot-remove" @click.stop="removeBookImage('coverBack')"><icon icon="fa-solid fa-xmark" /></button>
                          </template>
                          <template v-else>
                            <icon icon="fa-solid fa-plus" class="slot-add-icon" />
                            <span>Tải ảnh bìa sau</span>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              <!-- Navigation between books -->
              <div class="active-book-footer-nav mt-4" v-if="boothForm.totalBooks > 1">
                <button 
                  type="button" 
                  class="btn-prev-book"
                  :disabled="currentBookIndex === 0"
                  @click="currentBookIndex--"
                >
                  <icon icon="fa-solid fa-arrow-left" class="me-1" /> Cuốn trước
                </button>

                <span class="active-book-counter">Cuốn {{ currentBookIndex + 1 }} / {{ boothForm.totalBooks }}</span>

                <button 
                  type="button" 
                  class="btn-next-book"
                  :disabled="currentBookIndex >= boothForm.totalBooks - 1"
                  @click="currentBookIndex++"
                >
                  Cuốn tiếp theo <icon icon="fa-solid fa-arrow-right" class="ms-1" />
                </button>
              </div>

            </div>
          </section>

          <!-- Action Continue to Preview -->
          <div class="step1-bottom-actions">
            <button 
              type="button" 
              class="btn-proceed-preview"
              @click="goToPreview"
            >
              <span>Xem trước bài đăng</span>
              <icon icon="fa-solid fa-arrow-right" class="ms-2" />
            </button>
          </div>

        </div>

        <!-- STEP 2: LIVE PREVIEW STEP -->
        <div v-else-if="activeStep === 2" class="form-layout-step2">
          
          <!-- Sticky Top Bar -->
          <div class="preview-sticky-action-bar">
            <div class="action-bar-left">
              <span class="preview-status-pill">
                <icon icon="fa-solid fa-eye" class="me-2" /> BẢN XEM TRƯỚC GIAN HÀNG
              </span>
              <span class="action-bar-hint">Kiểm tra thông tin gian hàng và các cuốn sách trước khi đăng</span>
            </div>
            <div class="action-bar-right">
              <button 
                type="button" 
                class="btn-edit-back" 
                @click="activeStep = 1"
                :disabled="isSubmitting"
              >
                <icon icon="fa-solid fa-pen-to-square" class="me-2" /> Quay lại sửa
              </button>

              <button 
                type="button" 
                class="btn-confirm-submit" 
                @click="submitForm"
                :disabled="isSubmitting"
              >
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
                <icon v-else icon="fa-solid fa-circle-check" class="me-2" />
                {{ isSubmitting ? 'Đang lưu...' : (isEditMode ? 'Xác nhận Cập nhật' : 'Xác nhận Đăng bán') }}
              </button>
            </div>
          </div>

          <!-- Preview Content (Simulating old-book-detail) -->
          <div class="preview-booth-wrapper">
            <!-- Top Booth Card Preview -->
            <div class="booth-top-overview">
              <div class="gallery-col">
                <div class="main-image-frame" @click="openZoomModal(previewActiveImage)" title="Bấm để phóng to ảnh">
                  <img :src="previewActiveImage" :alt="effectiveBoothName" class="main-image" @error="handleImageError" />

                  <!-- Badges on Main Image (Top) -->
                  <span v-if="effectiveActivePreviewKey === 'stack'" class="detail-figure-badge stack-badge">
                    <icon icon="fa-solid fa-camera" class="me-1" />Ảnh chồng sách
                  </span>
                  <span v-else-if="activePreviewBookItem" class="detail-figure-badge cond-badge">
                    {{ getConditionLabel(activePreviewBookItem.verStatus) }} ({{ activePreviewBookItem.perStatus }}% mới)
                  </span>

                  <!-- Label Badge (Bottom Left) -->
                  <span class="main-image-badge" v-if="activePreviewLabel">
                    <icon icon="fa-solid fa-image" class="me-1" /> {{ activePreviewLabel }}
                  </span>

                  <!-- Zoom Hint Pill (Bottom Right) -->
                  <div class="gallery-zoom-hint">
                    <icon icon="fa-solid fa-magnifying-glass-plus" class="me-1" /> Phóng to ảnh
                  </div>
                </div>

                <!-- Thumbnail Carousel with Navigation Arrows -->
                <div class="gallery-thumb-carousel">
                  <button
                    type="button"
                    class="thumb-nav-btn btn-prev"
                    @click="scrollThumbs('left')"
                    title="Xem ảnh trước"
                  >
                    <icon icon="fa-solid fa-chevron-left" />
                  </button>

                  <div class="thumb-strip-scrollable" ref="thumbStrip" @wheel="handleThumbWheel">
                    <!-- Stack photo thumb -->
                    <button
                      v-if="stackPreview"
                      type="button"
                      class="thumb-btn stack-thumb"
                      :class="{ active: effectiveActivePreviewKey === 'stack' }"
                      @click="setPreviewMedia(stackPreview, 'Ảnh chụp chồng sách', 'stack')"
                      title="Ảnh chụp chồng sách"
                    >
                      <div class="thumb-img-wrap"><img :src="stackPreview" alt="" @error="handleImageError" /></div>
                      <span class="thumb-tag-badge stack-tag">Chồng sách</span>
                    </button>

                    <!-- Books thumbs: Cover front & Cover back -->
                    <template v-for="(b, idx) in books" :key="'prev-thumbs-' + idx">
                      <button
                        type="button"
                        class="thumb-btn"
                        :class="{ active: effectiveActivePreviewKey === ('book_' + idx + '_cover') }"
                        v-if="bookPreviews[idx] && bookPreviews[idx].cover"
                        @click="setPreviewMedia(bookPreviews[idx].cover, 'Cuốn ' + (idx + 1) + ' - Bìa trước', 'book_' + idx + '_cover')"
                        :title="b.name + ' - Bìa trước'"
                      >
                        <div class="thumb-img-wrap"><img :src="bookPreviews[idx].cover" alt="" @error="handleImageError" /></div>
                        <span class="thumb-tag-badge">C.{{ idx + 1 }} trước</span>
                      </button>

                      <button
                        type="button"
                        class="thumb-btn"
                        :class="{ active: effectiveActivePreviewKey === ('book_' + idx + '_coverBack') }"
                        v-if="bookPreviews[idx] && bookPreviews[idx].coverBack"
                        @click="setPreviewMedia(bookPreviews[idx].coverBack, 'Cuốn ' + (idx + 1) + ' - Bìa sau', 'book_' + idx + '_coverBack')"
                        :title="b.name + ' - Bìa sau'"
                      >
                        <div class="thumb-img-wrap"><img :src="bookPreviews[idx].coverBack" alt="" @error="handleImageError" /></div>
                        <span class="thumb-tag-badge">C.{{ idx + 1 }} sau</span>
                      </button>
                    </template>
                  </div>

                  <button
                    type="button"
                    class="thumb-nav-btn btn-next"
                    @click="scrollThumbs('right')"
                    title="Xem ảnh tiếp theo"
                  >
                    <icon icon="fa-solid fa-chevron-right" />
                  </button>
                </div>
              </div>

              <div class="info-col">
                <h1 class="booth-title font-serif">{{ effectiveBoothName }}</h1>
                <div class="booth-meta-strip">
                  <span class="meta-badge count-badge">
                    <icon :icon="boothForm.totalBooks > 1 ? 'fa-solid fa-layer-group' : 'fa-solid fa-book'" class="me-1" />
                    {{ boothForm.totalBooks > 1 ? ('Combo ' + boothForm.totalBooks + ' cuốn') : '1 cuốn sách' }}
                  </span>
                  <span class="meta-badge sale-type-badge" :class="boothForm.saleType === 1 ? 'type-combo' : 'type-single'">
                    {{ boothForm.saleType === 1 ? 'Bắt buộc mua cả combo' : 'Cho phép mua lẻ từng cuốn' }}
                  </span>
                  <span v-if="boothForm.weight > 0" class="meta-badge weight-badge">
                    <icon icon="fa-solid fa-weight-hanging" class="me-1 opacity-75" /> Khối lượng: {{ formatWeight(boothForm.weight) }}
                  </span>
                  <span class="meta-badge status-live-badge">
                    <span class="live-dot"></span> Đang bán
                  </span>
                </div>

                <div class="price-box">
                  <div class="price-box-main">
                    <span class="price-box-label">{{ boothForm.saleType === 1 ? 'Giá Bán Trọn Gói Combo' : 'Tổng Giá Trị Gian Hàng' }}</span>
                    <div class="d-flex align-items-baseline gap-3 mt-1 flex-wrap">
                      <strong class="price-amount-hero">{{ formatPrice(effectivePrice) }}</strong>
                      <span v-if="calculatedDiscountRate > 0" class="price-original-strikethrough">
                        {{ formatPrice(calculatedTotalOriginalPrice) }}
                      </span>
                      <span v-if="calculatedDiscountRate > 0" class="sale-off-hero-badge">
                        -{{ calculatedDiscountRate }}% SALE OFF
                      </span>
                    </div>
                  </div>
                  <div class="price-box-note" v-if="calculatedDiscountRate > 0">
                    <icon icon="fa-solid fa-circle-check" class="text-emerald-600 me-1" />
                    Tiết kiệm được <strong>{{ formatPrice(calculatedTotalOriginalPrice - effectivePrice) }}</strong> so với giá bìa gốc!
                  </div>
                </div>
                <!-- If 1 book: Display its specs & description directly in the overview -->
                <div class="single-book-overview-meta mt-3" v-if="books.length === 1">
                  <div class="book-specs-strip">
                    <div class="spec-pill" v-if="books[0].author">
                      <span class="spec-lbl">Tác giả:</span> <strong>{{ books[0].author }}</strong>
                    </div>
                    <div class="spec-pill">
                      <span class="spec-lbl">Độ mới:</span> <strong>{{ books[0].perStatus }}%</strong>
                    </div>
                    <div class="spec-pill">
                      <span class="spec-lbl">Tình trạng:</span> <strong>{{ getConditionLabel(books[0].verStatus) }}</strong>
                    </div>
                    <div class="spec-pill" v-if="books[0].totalPage">
                      <span class="spec-lbl">Số trang:</span> <span>{{ books[0].totalPage }}</span>
                    </div>
                  </div>

                  <div class="preview-cat-tags mt-2" v-if="getBookCategoriesList(books[0]).length > 0">
                    <span v-for="cat in getBookCategoriesList(books[0])" :key="'single-cat-' + cat.value" class="preview-cat-chip">
                      {{ cat.label }}
                    </span>
                  </div>

                  <div class="booth-desc-section mt-3" v-if="books[0].description">
                    <h3 class="section-sub-title">Mô tả tình trạng sách</h3>
                    <p class="desc-content">{{ books[0].description }}</p>
                  </div>
                </div>

                <!-- If multiple books: Display general booth description -->
                <div class="booth-desc-section mt-3" v-else-if="boothForm.description">
                  <h3 class="section-sub-title">Ghi chú từ người bán</h3>
                  <p class="desc-content">{{ boothForm.description }}</p>
                </div>

                <!-- Seller & Primary CTA Box -->
                <div class="seller-cta-box">
                  <div class="seller-profile">
                    <div class="seller-avatar-wrap">
                      <icon icon="fa-solid fa-store" />
                    </div>
                    <div class="seller-details">
                      <h4 class="seller-name">{{ sellerDisplayName }}</h4>
                      <span class="seller-date">Đăng ngày {{ formattedCreatedDate }}</span>
                    </div>
                  </div>

                  <button 
                    type="button" 
                    class="btn-primary-buy"
                    disabled
                  >
                    <icon icon="fa-solid fa-cart-shopping" class="me-2" />
                    <span>{{ boothForm.saleType === 1 ? 'Mua trọn bộ Combo' : 'Đặt mua gian hàng này' }}</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Preview List of Books (only when books.length > 1) -->
            <section class="booth-books-list-section mt-4" v-if="books.length > 1">
              <div class="section-header-row">
                <div>
                  <h2 class="section-main-title font-serif">
                    <icon icon="fa-solid fa-boxes-stacked" class="text-emerald-600 me-2" />
                    Chi tiết từng cuốn sách trong gian hàng
                  </h2>
                  <p class="section-main-subtitle">
                    Xem hình ảnh thực tế bìa trước, bìa sau, độ mới và mô tả chi tiết của từng cuốn sách
                  </p>
                </div>
                <span class="section-count-tag">
                  Tổng số: <strong>{{ books.length }}</strong> cuốn
                </span>
              </div>

              <div class="book-items-grid">
                <div v-for="(b, idx) in books" :key="'p-book-' + idx" class="book-item-card">
                  <div class="book-item-header">
                    <div class="book-item-index">{{ idx + 1 }}</div>
                    <div class="book-item-title-group">
                      <h3 class="book-item-title font-serif">{{ b.name }}</h3>
                      <div class="book-item-meta">
                        <span v-if="b.author" class="item-meta-author">
                          <icon icon="fa-solid fa-feather-pointed" class="me-1" />{{ b.author }}
                        </span>
                        <span v-if="getBookCategoriesList(b).length > 0" class="item-meta-category">
                          <icon icon="fa-solid fa-tag" class="me-1" />{{ getBookCategoriesList(b).map(c => c.label).join(', ') }}
                        </span>
                      </div>
                    </div>

                    <router-link
                      v-if="b.idBook"
                      :to="'/book/' + b.idBook"
                      class="btn-view-catalog-link"
                      title="Xem sách gốc trên hệ thống"
                      target="_blank"
                    >
                      <icon icon="fa-solid fa-book-open" class="me-1" /> Sách gốc
                    </router-link>
                  </div>

                  <div class="book-item-body">
                    <div class="book-item-photos">
                      <div
                        class="photo-box"
                        v-if="bookPreviews[idx] && bookPreviews[idx].cover"
                        @click="openZoomModal(bookPreviews[idx].cover)"
                        title="Bấm để xem ảnh lớn"
                      >
                        <img :src="bookPreviews[idx].cover" alt="Bìa trước" />
                        <span class="photo-box-label">Bìa trước</span>
                        <span class="photo-zoom-icon"><icon icon="fa-solid fa-magnifying-glass-plus" /></span>
                      </div>
                      <div
                        class="photo-box"
                        v-if="bookPreviews[idx] && bookPreviews[idx].coverBack"
                        @click="openZoomModal(bookPreviews[idx].coverBack)"
                        title="Bấm để xem ảnh lớn"
                      >
                        <img :src="bookPreviews[idx].coverBack" alt="Bìa sau" />
                        <span class="photo-box-label">Bìa sau</span>
                        <span class="photo-zoom-icon"><icon icon="fa-solid fa-magnifying-glass-plus" /></span>
                      </div>
                    </div>

                    <div class="book-item-details">
                      <div class="book-specs-strip">
                        <div class="spec-pill">
                          <span class="spec-lbl">Độ mới</span>
                          <strong class="spec-val text-emerald-600">{{ b.perStatus }}%</strong>
                        </div>
                        <div class="spec-pill">
                          <span class="spec-lbl">Tình trạng</span>
                          <strong class="spec-val" :class="'cond-text-' + b.verStatus">{{ getConditionLabel(b.verStatus) }}</strong>
                        </div>
                        <div class="spec-pill" v-if="b.totalPage > 0">
                          <span class="spec-lbl">Số trang</span>
                          <strong class="spec-val">{{ b.totalPage }}</strong>
                        </div>
                        <div class="spec-pill" v-if="b.originalPrice && b.originalPrice > 0">
                          <span class="spec-lbl">Giá bìa</span>
                          <strong class="spec-val text-muted">{{ formatPrice(b.originalPrice) }}</strong>
                        </div>
                      </div>

                      <div class="book-desc-block">
                        <span class="desc-lbl">Mô tả tình trạng:</span>
                        <p class="desc-txt">{{ b.description || 'Sách giữ gìn tốt.' }}</p>
                      </div>

                      <div class="book-item-footer">
                        <div class="book-item-price-block">
                          <template v-if="boothForm.saleType === 0 && b.price">
                            <span class="price-lbl">Giá bán lẻ:</span>
                            <strong class="price-val">{{ formatPrice(b.price) }}</strong>
                          </template>
                          <template v-else>
                            <span class="price-combo-note">Thuộc bộ combo</span>
                          </template>
                        </div>

                        <button
                          v-if="boothForm.saleType === 0 && b.price"
                          type="button"
                          class="btn-buy-single"
                          disabled
                        >
                          <icon icon="fa-solid fa-bag-shopping" class="me-1" />
                          <span>Mua lẻ cuốn này</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

          </div>
        </div>

        <!-- STEP 3: COMPLETION / SUCCESS STEP -->
        <div v-else-if="activeStep === 3" class="form-layout-step3">
          <div class="completion-card">
            <div class="completion-icon-box">
              <icon icon="fa-solid fa-check" />
            </div>

            <h2 class="completion-title font-serif">{{ isEditMode ? 'Cập nhật gian hàng thành công!' : 'Đăng bán gian hàng sách cũ thành công!' }}</h2>
            <p class="completion-desc">
              Bài đăng của bạn đã được xuất bản và sẵn sàng để độc giả trên toàn quốc tìm kiếm, liên hệ và giao dịch.
            </p>

            <!-- Summary Box -->
            <div class="completion-summary-box">
              <div class="summary-thumb-box">
                <img :src="previewActiveImage" :alt="effectiveBoothName" @error="handleImageError" />
              </div>
              <div class="summary-details">
                <h3 class="summary-booth-name font-serif">{{ effectiveBoothName }}</h3>
                <div class="summary-badges-row">
                  <span class="meta-badge count-badge">
                    <icon :icon="boothForm.totalBooks > 1 ? 'fa-solid fa-layer-group' : 'fa-solid fa-book'" class="me-1" />
                    {{ boothForm.totalBooks > 1 ? ('Combo ' + boothForm.totalBooks + ' cuốn') : '1 cuốn sách' }}
                  </span>
                  <span class="meta-badge sale-type-badge" :class="boothForm.saleType === 1 ? 'type-combo' : 'type-single'">
                    {{ boothForm.saleType === 1 ? 'Bắt buộc mua combo' : 'Cho phép mua lẻ từng cuốn' }}
                  </span>
                </div>
                <div class="summary-price-tag">
                  <span class="price-lbl">Giá bán:</span>
                  <strong class="price-val">{{ formatPrice(effectivePrice) }}</strong>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="completion-actions">
              <button 
                type="button" 
                class="btn-complete-primary"
                @click="goToBoothDetail"
              >
                <icon icon="fa-solid fa-arrow-up-right-from-square" class="me-2" /> Xem bài đăng vừa tạo
              </button>

              <router-link to="/my-old-books" class="btn-complete-secondary">
                <icon icon="fa-solid fa-store" class="me-2" /> Quản lý gian hàng của tôi
              </router-link>

              <button 
                type="button" 
                class="btn-complete-ghost"
                @click="resetForm"
              >
                <icon icon="fa-solid fa-plus" class="me-2" /> Đăng thêm gian hàng khác
              </button>
            </div>
          </div>
        </div>

      </div>
    </main>

    <!-- Zoom Full Image Modal (Interactive Zoom In / Out / Pan / Wheel) -->
    <div v-if="showZoomModal" class="zoom-overlay" @click="closeZoomModal" @wheel.prevent="handleWheelZoom">
      <!-- Floating Control Toolbar -->
      <div class="zoom-toolbar" :class="{ 'zoom-toolbar-hidden': toolbarHidden }" @click.stop>
        <button class="zoom-tool-btn" @click="zoomOut" title="Thu nhỏ (-)" :disabled="zoomScale <= 0.5">
          <icon icon="fa-solid fa-minus" />
        </button>
        <span class="zoom-percent-badge">{{ Math.round(zoomScale * 100) }}%</span>
        <button class="zoom-tool-btn" @click="zoomIn" title="Phóng to (+)" :disabled="zoomScale >= 4">
          <icon icon="fa-solid fa-plus" />
        </button>
        <div class="zoom-tool-divider"></div>
        <button class="zoom-tool-btn" @click="resetZoom" title="Đặt lại kích thước (100%)">
          <icon icon="fa-solid fa-rotate-left" />
        </button>
        <button class="zoom-tool-btn close" @click="closeZoomModal" title="Đóng cửa sổ">
          <icon icon="fa-solid fa-xmark" />
        </button>
      </div>

      <!-- Zoom Image Stage -->
      <div 
        ref="zoomStage"
        class="zoom-content-stage" 
        @click.stop
        @dblclick="toggleDblZoom"
        @mousedown="startDrag"
        @mousemove="onDrag"
        @mouseup="stopDrag"
        @mouseleave="stopDrag"
      >
        <img 
          :src="zoomImage" 
          :alt="effectiveBoothName" 
          @error="handleImageError" 
          :style="{
            transform: 'scale(' + zoomScale + ') translate(' + (zoomPanX / zoomScale) + 'px, ' + (zoomPanY / zoomScale) + 'px)',
            cursor: zoomScale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in'
          }"
          :class="['zoom-image-element', { 'zoom-dragging': isDragging }]"
        />
      </div>

      <div class="zoom-hint-pill">
        <icon icon="fa-solid fa-circle-info" class="me-1" /> Lăn chuột hoặc phím + / - để phóng to · Nhấn đúp để phóng 200% · Kéo chuột để di chuyển ảnh · Esc để đóng
      </div>
    </div>

    <Footer />
  </div>
</template>

<script src="./old-book-form.js"></script>
<style scoped src="./old-book-form.css"></style>
